import { z } from 'zod';
export const estimatingPricebookCapability = 'estimating.pricebook.v1';
export const estimatingPricebookVersionsCapability = 'estimating.pricebook-versions.v1';
export const estimatingPricebookImportsCapability = 'estimating.pricebook-imports.v1';
export const estimatingPricebookCategoryIdSchema = z.string().uuid();
export const estimatingPricebookItemIdSchema = z.string().uuid();
export const estimatingPricebookImportIdSchema = z.string().uuid();
export const estimatingPricebookMoneyCentsSchema = z.number().int().min(0).max(999_999_999_999);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const commandReason = z.string().trim().min(10).max(500);
export const estimatingPricebookSourceReferenceSchema = z.strictObject({
    sourceCatalog: z.string().trim().min(1).max(120),
    sourceKey: z.string().trim().min(1).max(240),
    priceListCode: nullableText(120).default(null),
    pricingFormulaVersion: nullableText(120).default(null),
    externalCode: nullableText(120).default(null),
    externalBaseCode: nullableText(120).default(null),
    externalCategoryCode: nullableText(120).default(null),
}).nullable();
export const estimatingPricebookCategoryDraftSchema = z.strictObject({
    name: z.string().trim().min(1).max(160),
    description: nullableText(1_000).default(null),
    externalCode: nullableText(120).default(null),
    sortOrder: z.number().int().min(0).max(1_000_000).default(0),
});
export const estimatingPricebookCategorySchema = z.strictObject({
    contractVersion: z.literal(estimatingPricebookCapability),
    tenantId: z.string().uuid(),
    id: estimatingPricebookCategoryIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    data: estimatingPricebookCategoryDraftSchema,
});
export const estimatingPricebookItemDraftSchema = z.strictObject({
    categoryId: estimatingPricebookCategoryIdSchema.nullable().default(null),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000).default(null),
    details: nullableText(12_000).default(null),
    roomTypes: z.array(z.string().trim().min(1).max(120)).max(100).default([]),
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: estimatingPricebookMoneyCentsSchema,
    taxable: z.boolean().default(false),
    active: z.boolean().default(true),
    sku: nullableText(160).default(null),
    sourceReference: estimatingPricebookSourceReferenceSchema.default(null),
});
export const estimatingPricebookItemVersionSchema = z.strictObject({
    contractVersion: z.literal(estimatingPricebookVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    pricebookItemId: estimatingPricebookItemIdSchema,
    versionNumber: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: commandReason,
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: estimatingPricebookItemDraftSchema,
});
export const estimatingPricebookItemSchema = z.strictObject({
    contractVersion: z.literal(estimatingPricebookCapability),
    tenantId: z.string().uuid(),
    id: estimatingPricebookItemIdSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: estimatingPricebookItemVersionSchema,
});
const createCategoryCommand = z.strictObject({
    kind: z.literal('create'), commandId: z.string().uuid(),
    categoryId: estimatingPricebookCategoryIdSchema,
    category: estimatingPricebookCategoryDraftSchema, reason: commandReason,
});
const reviseCategoryCommand = z.strictObject({
    kind: z.literal('revise'), commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
    category: estimatingPricebookCategoryDraftSchema, reason: commandReason,
});
const categoryStateCommand = z.strictObject({
    kind: z.enum(['archive', 'restore']), commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(), reason: commandReason,
});
export const estimatingPricebookCreateCategoryCommandSchema = createCategoryCommand;
export const estimatingPricebookCategoryCommandSchema = z.discriminatedUnion('kind', [
    reviseCategoryCommand, categoryStateCommand,
]);
const createItemCommand = z.strictObject({
    kind: z.literal('create'), commandId: z.string().uuid(),
    pricebookItemId: estimatingPricebookItemIdSchema,
    item: estimatingPricebookItemDraftSchema, reason: commandReason,
});
const reviseItemCommand = z.strictObject({
    kind: z.literal('revise'), commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
    item: estimatingPricebookItemDraftSchema, reason: commandReason,
});
const itemStateCommand = z.strictObject({
    kind: z.enum(['archive', 'restore']), commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(), reason: commandReason,
});
export const estimatingPricebookCreateItemCommandSchema = createItemCommand;
export const estimatingPricebookItemCommandSchema = z.discriminatedUnion('kind', [
    reviseItemCommand, itemStateCommand,
]);
export const estimatingPricebookListQuerySchema = z.strictObject({
    query: z.string().trim().max(200).default(''),
    categoryId: estimatingPricebookCategoryIdSchema.nullable().default(null),
    includeInactive: z.boolean().default(false),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(60),
    offset: z.number().int().nonnegative().max(50_000).default(0),
});
export const estimatingPricebookCsvImportCommandSchema = z.strictObject({
    kind: z.literal('import-csv'),
    commandId: z.string().uuid(),
    importId: estimatingPricebookImportIdSchema,
    csv: z.string().min(1).max(2_000_000),
    reason: commandReason,
});
export const estimatingPricebookImportRowSchema = estimatingPricebookItemDraftSchema.extend({
    rowNumber: z.number().int().min(2),
    categoryName: nullableText(160).default(null),
    categoryExternalCode: nullableText(120).default(null),
});
export const estimatingPricebookImportReceiptSchema = z.strictObject({
    contractVersion: z.literal(estimatingPricebookImportsCapability),
    tenantId: z.string().uuid(),
    id: estimatingPricebookImportIdSchema,
    commandId: z.string().uuid(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    rowCount: z.number().int().nonnegative(),
    createdCount: z.number().int().nonnegative(),
    updatedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    results: z.array(z.strictObject({
        rowNumber: z.number().int().min(2),
        outcome: z.enum(['created', 'updated', 'skipped', 'error']),
        pricebookItemId: estimatingPricebookItemIdSchema.nullable(),
        message: nullableText(1_000),
    })),
    actorMembershipId: z.string().uuid(),
    completedAt: z.string().datetime({ offset: true }),
});
const pageSchema = z.strictObject({
    limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
});
const commandReceiptSchema = z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() });
export const estimatingPricebookCategoryResponseSchema = z.strictObject({
    data: estimatingPricebookCategorySchema, command: commandReceiptSchema.optional(),
});
export const estimatingPricebookCategoriesResponseSchema = z.strictObject({
    data: z.array(estimatingPricebookCategorySchema),
});
export const estimatingPricebookItemResponseSchema = z.strictObject({
    data: estimatingPricebookItemSchema, command: commandReceiptSchema.optional(),
});
export const estimatingPricebookItemsResponseSchema = z.strictObject({
    data: z.array(estimatingPricebookItemSchema), page: pageSchema,
});
export const estimatingPricebookItemVersionsResponseSchema = z.strictObject({
    data: z.array(estimatingPricebookItemVersionSchema),
});
export const estimatingPricebookImportResponseSchema = z.strictObject({
    data: estimatingPricebookImportReceiptSchema,
    command: commandReceiptSchema,
});
export const estimatingPricebookSummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(estimatingPricebookCapability),
        categoryCount: z.number().int().nonnegative(),
        totalItemCount: z.number().int().nonnegative(),
        activeItemCount: z.number().int().nonnegative(),
        inactiveItemCount: z.number().int().nonnegative(),
        archivedItemCount: z.number().int().nonnegative(),
        currentPriceListCode: nullableText(120),
        lastImportedAt: z.string().datetime({ offset: true }).nullable(),
        lastImportId: estimatingPricebookImportIdSchema.nullable(),
    }),
});
export const estimatingPricebookCsvExportResponseSchema = z.strictObject({
    data: z.strictObject({
        fileName: z.string().trim().min(1).max(240),
        contentType: z.literal('text/csv; charset=utf-8'),
        contentHash: z.string().regex(/^[a-f0-9]{64}$/),
        csv: z.string(),
    }),
});
//# sourceMappingURL=estimating-pricebook.contract.js.map